import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const FALLBACK_CURRENCY_CODES = [
  "INR",
  "USD",
  "EUR",
  "GBP",
  "AED",
  "AUD",
  "BDT",
  "CAD",
  "CHF",
  "CNY",
  "HKD",
  "IDR",
  "JPY",
  "KES",
  "KRW",
  "KWD",
  "LKR",
  "MAD",
  "MXN",
  "MYR",
  "NGN",
  "NOK",
  "NPR",
  "NZD",
  "OMR",
  "PHP",
  "PKR",
  "QAR",
  "RUB",
  "SAR",
  "SEK",
  "SGD",
  "THB",
  "TRY",
  "UGX",
  "VND",
  "ZAR",
];

function getDisplayNames() {
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") return null;
  try {
    return new Intl.DisplayNames(["en"], { type: "currency" });
  } catch {
    return null;
  }
}

function getSupportedCurrencyCodes() {
  const fallback = [...new Set(FALLBACK_CURRENCY_CODES)];
  if (typeof Intl === "undefined" || typeof Intl.supportedValuesOf !== "function") {
    return fallback;
  }

  try {
    const codes = Intl.supportedValuesOf("currency")
      .map((value) => String(value || "").trim().toUpperCase())
      .filter((value) => /^[A-Z]{3}$/.test(value));
    return [...new Set(["INR", ...codes, ...fallback])];
  } catch {
    return fallback;
  }
}

function normalizeCurrencyCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : "";
}

function getCurrencySymbol(code) {
  const normalizedCode = normalizeCurrencyCode(code);
  if (!normalizedCode || typeof Intl === "undefined" || typeof Intl.NumberFormat !== "function") {
    return normalizedCode;
  }

  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: normalizedCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const symbol = parts.find((part) => part.type === "currency")?.value || normalizedCode;
    return symbol.toUpperCase() === normalizedCode ? normalizedCode : symbol;
  } catch {
    return normalizedCode;
  }
}

function buildCurrencyOptions() {
  const displayNames = getDisplayNames();
  return getSupportedCurrencyCodes()
    .map((code) => {
      const name = displayNames?.of(code) || code;
      const symbol = getCurrencySymbol(code) || code;
      return {
        id: code,
        code,
        name,
        symbol,
        searchText: `${code} ${name} ${symbol}`.toLowerCase(),
      };
    })
    .sort((left, right) => {
      if (left.code === "INR") return -1;
      if (right.code === "INR") return 1;
      return left.name.localeCompare(right.name) || left.code.localeCompare(right.code);
    });
}

const CURRENCY_OPTIONS = buildCurrencyOptions();
const AMOUNT_UNIT_OPTIONS = [
  { value: "thousand", label: "Thousand", multiplier: 1000 },
  { value: "lakh", label: "Lakh", multiplier: 100000 },
  { value: "crore", label: "Crore", multiplier: 10000000 },
];
const MAX_STORED_AMOUNT = 9999999999999;

export { CURRENCY_OPTIONS };

export function getCurrencyOption(value) {
  const code = normalizeCurrencyCode(value);
  if (!code) return null;
  return (
    CURRENCY_OPTIONS.find((option) => option.code === code) || {
      id: code,
      code,
      name: code,
      symbol: getCurrencySymbol(code) || code,
      searchText: code.toLowerCase(),
    }
  );
}

export function getCurrencyName(value) {
  return getCurrencyOption(value)?.name || "";
}

export function formatCurrencyAmount(amount, currencyCode = "INR", locale = "en-IN") {
  if (amount === null || amount === undefined || amount === "") return "";
  const code = normalizeCurrencyCode(currencyCode) || "INR";
  const number = Number(amount);

  if (!Number.isFinite(number)) {
    return String(amount);
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(number);
  } catch {
    const option = getCurrencyOption(code);
    return `${option?.symbol || code} ${number.toLocaleString("en-IN")}`.trim();
  }
}

export function normalizeOccupationCurrencyCode(value, amount) {
  if (amount === null || amount === undefined || amount === "") return null;
  return normalizeCurrencyCode(value) || "INR";
}

export function CurrencySelect({
  value,
  onChange,
  placeholder = "Select currency",
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const selectedOption = getCurrencyOption(value);
  const normalizedQuery = query.trim().toLowerCase();

  const visibleOptions = useMemo(() => {
    if (!normalizedQuery) return CURRENCY_OPTIONS;
    return CURRENCY_OPTIONS.filter((option) => option.searchText.includes(normalizedQuery));
  }, [normalizedQuery]);

  const updateMenuPosition = useCallback(() => {
    if (!anchorRef.current || typeof window === "undefined") return;

    const rect = anchorRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuHeight = menuRef.current?.offsetHeight || 340;
    const width = Math.max(rect.width, 248);
    const left = Math.min(Math.max(12, rect.left), viewportWidth - width - 12);
    const spaceBelow = viewportHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUpward = spaceBelow < Math.min(menuHeight, 320) && spaceAbove > spaceBelow;
    const top = openUpward
      ? Math.max(12, rect.top - menuHeight - 8)
      : Math.max(12, Math.min(viewportHeight - menuHeight - 12, rect.bottom + 8));

    setMenuStyle({
      position: "fixed",
      top,
      left,
      width,
      zIndex: 10050,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();
    const handleWindowChange = () => updateMenuPosition();
    const handlePointerDown = (event) => {
      const target = event.target;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    searchRef.current?.focus();
  }, [open, visibleOptions.length, updateMenuPosition]);

  return (
    <div ref={anchorRef} style={{ ...styles.currencyDropdown, ...(disabled ? styles.currencyDropdownDisabled : {}) }}>
      <button
        type="button"
        style={{ ...styles.currencySummary, ...(disabled ? styles.currencySummaryDisabled : {}) }}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((current) => !current);
        }}
      >
        {selectedOption ? (
          <span style={styles.currencySummaryLabel}>
            <span style={styles.currencyBadge}>{selectedOption.symbol}</span>
            <span>{selectedOption.code} - {selectedOption.name}</span>
          </span>
        ) : (
          <span style={styles.currencyPlaceholder}>{placeholder}</span>
        )}
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div ref={menuRef} style={{ ...styles.currencyMenu, ...menuStyle }}>
          <input
            ref={searchRef}
            type="text"
            style={styles.currencySearch}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search currency..."
          />

          <div style={styles.currencyOptions}>
            {visibleOptions.length === 0 ? (
              <div style={styles.currencyEmpty}>No currencies found.</div>
            ) : (
              visibleOptions.map((option) => {
                const active = option.code === selectedOption?.code;
                return (
                  <button
                    key={option.code}
                    type="button"
                    style={{ ...styles.currencyOption, ...(active ? styles.currencyOptionActive : {}) }}
                    onClick={() => {
                      onChange?.(option.code);
                      setQuery("");
                      setOpen(false);
                    }}
                  >
                    <span style={styles.currencyOptionMain}>
                      <span style={styles.currencyBadge}>{option.symbol}</span>
                      <span>{option.name}</span>
                    </span>
                    <span style={styles.currencyCode}>{option.code}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function CurrencyAmountInput({
  amount,
  currencyCode,
  onAmountChange,
  onCurrencyChange,
  placeholder,
  disabled = false,
}) {
  const [displayAmount, setDisplayAmount] = useState("");
  const [unit, setUnit] = useState("lakh");
  const displayAmountRef = useRef(displayAmount);
  const unitRef = useRef(unit);
  const normalizedStoredAmount = useMemo(() => normalizeStoredAmountValue(amount), [amount]);

  useEffect(() => {
    displayAmountRef.current = displayAmount;
  }, [displayAmount]);

  useEffect(() => {
    unitRef.current = unit;
  }, [unit]);

  useEffect(() => {
    const currentStoredValue = computeStoredAmountFromInput(displayAmountRef.current, unitRef.current);
    if (normalizedStoredAmount === currentStoredValue) return;

    if (!normalizedStoredAmount) {
      setDisplayAmount("");
      setUnit("lakh");
      return;
    }

    const preferredUnit = pickPreferredAmountUnit(normalizedStoredAmount);
    setUnit(preferredUnit.value);
    setDisplayAmount(convertStoredAmountToDisplay(normalizedStoredAmount, preferredUnit.multiplier));
  }, [normalizedStoredAmount]);

  const handleAmountChange = (event) => {
    const nextDisplayAmount = sanitizeDecimalInput(event?.target?.value ?? event ?? "");
    setDisplayAmount(nextDisplayAmount);
    onAmountChange?.(computeStoredAmountFromInput(nextDisplayAmount, unit));
  };

  const handleUnitChange = (event) => {
    const nextUnit = String(event?.target?.value || "lakh");
    const currentStoredAmount = computeStoredAmountFromInput(displayAmount, unit);
    setUnit(nextUnit);

    if (!currentStoredAmount) {
      onAmountChange?.("");
      return;
    }

    const nextMultiplier = getAmountUnitMultiplier(nextUnit);
    setDisplayAmount(convertStoredAmountToDisplay(currentStoredAmount, nextMultiplier));
    onAmountChange?.(currentStoredAmount);
  };

  return (
    <div style={styles.amountRow}>
      <div style={styles.amountCurrencySlot}>
        <CurrencySelect
          value={currencyCode || "INR"}
          onChange={onCurrencyChange}
          placeholder="Currency"
          disabled={disabled}
        />
      </div>
      <input
        type="text"
        inputMode="decimal"
        style={{ ...styles.amountInput, ...(disabled ? styles.amountInputDisabled : {}) }}
        value={displayAmount}
        onChange={handleAmountChange}
        placeholder={placeholder}
        disabled={disabled}
      />
      <select
        style={{ ...styles.amountUnitSelect, ...(disabled ? styles.amountInputDisabled : {}) }}
        value={unit}
        onChange={handleUnitChange}
        disabled={disabled}
        aria-label="Amount unit"
      >
        {AMOUNT_UNIT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}

function sanitizeDecimalInput(value) {
  const normalized = String(value ?? "").replace(/,/g, "").replace(/[^\d.]/g, "");
  const firstDotIndex = normalized.indexOf(".");
  if (firstDotIndex === -1) return normalized;
  const beforeDot = normalized.slice(0, firstDotIndex + 1);
  const afterDot = normalized.slice(firstDotIndex + 1).replace(/\./g, "");
  return `${beforeDot}${afterDot}`;
}

function normalizeStoredAmountValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  return trimTrailingZeros(String(Math.min(numeric, MAX_STORED_AMOUNT)));
}

function getAmountUnitMultiplier(unit) {
  return AMOUNT_UNIT_OPTIONS.find((option) => option.value === unit)?.multiplier || 100000;
}

function trimTrailingZeros(value) {
  const normalized = String(value || "").trim();
  if (!normalized.includes(".")) return normalized;
  return normalized.replace(/(\.\d*?[1-9])0+$/u, "$1").replace(/\.0+$/u, "");
}

function computeStoredAmountFromInput(displayAmount, unit) {
  const cleanValue = sanitizeDecimalInput(displayAmount);
  if (!cleanValue || cleanValue === ".") return "";

  const numeric = Number.parseFloat(cleanValue);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";

  const multiplier = getAmountUnitMultiplier(unit);
  const storedAmount = Math.min(numeric * multiplier, MAX_STORED_AMOUNT);
  return trimTrailingZeros(String(storedAmount));
}

function convertStoredAmountToDisplay(storedAmount, multiplier) {
  const numeric = Number(storedAmount);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  return trimTrailingZeros(String(numeric / multiplier));
}

function pickPreferredAmountUnit(storedAmount) {
  const numeric = Number(storedAmount);
  if (!Number.isFinite(numeric) || numeric <= 0) return AMOUNT_UNIT_OPTIONS[0];

  const orderedUnits = [...AMOUNT_UNIT_OPTIONS].reverse();
  return orderedUnits.find((option) => {
    const converted = numeric / option.multiplier;
    return converted >= 1;
  }) || AMOUNT_UNIT_OPTIONS[0];
}

const styles = {
  amountRow: {
    display: "flex",
    flexWrap: "nowrap",
    gap: 12,
    alignItems: "stretch",
  },
  amountInput: {
    flex: "1 1 180px",
    minWidth: 0,
    width: "100%",
    height: 50,
    borderRadius: 18,
    border: "1px solid rgba(132,42,45,0.16)",
    padding: "0 16px",
    fontSize: 15,
    outline: "none",
    background: "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,251,250,0.98) 100%)",
    color: "#2f2833",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.75)",
    boxSizing: "border-box",
  },
  amountInputDisabled: {
    background: "#f7f4f2",
    color: "#7f8795",
    cursor: "not-allowed",
  },
  amountCurrencySlot: {
    flex: "0 0 248px",
    minWidth: 0,
  },
  amountUnitSelect: {
    flex: "0 0 136px",
    minWidth: 0,
    height: 50,
    borderRadius: 18,
    border: "1px solid rgba(132,42,45,0.16)",
    padding: "0 40px 0 16px",
    fontSize: 14,
    fontWeight: 700,
    outline: "none",
    background: "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,251,250,0.98) 100%)",
    color: "#2f2833",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, rgba(132,42,45,0.65) 50%), linear-gradient(135deg, rgba(132,42,45,0.65) 50%, transparent 50%), linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,251,250,0.98) 100%)",
    backgroundPosition: "calc(100% - 20px) calc(50% - 3px), calc(100% - 14px) calc(50% - 3px), 0 0",
    backgroundSize: "6px 6px, 6px 6px, 100% 100%",
    backgroundRepeat: "no-repeat",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.75)",
    boxSizing: "border-box",
  },
  currencyDropdown: {
    width: "100%",
    position: "relative",
    display: "grid",
    alignContent: "start",
  },
  currencyDropdownDisabled: {
    opacity: 0.8,
  },
  currencySummary: {
    width: "100%",
    border: "1px solid rgba(132,42,45,0.16)",
    height: 50,
    padding: "0 42px 0 14px",
    borderRadius: 18,
    background: "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,251,250,0.98) 100%)",
    cursor: "pointer",
    color: "#2f2833",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    boxSizing: "border-box",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.75)",
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, rgba(132,42,45,0.62) 50%), linear-gradient(135deg, rgba(132,42,45,0.62) 50%, transparent 50%), linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,251,250,0.98) 100%)",
    backgroundPosition: "calc(100% - 20px) calc(50% - 3px), calc(100% - 14px) calc(50% - 3px), 0 0",
    backgroundSize: "6px 6px, 6px 6px, 100% 100%",
    backgroundRepeat: "no-repeat",
    textAlign: "left",
  },
  currencySummaryDisabled: {
    cursor: "not-allowed",
    background: "#f7f4f2",
  },
  currencySummaryLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 700,
    minWidth: 0,
    width: "100%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  currencyPlaceholder: {
    color: "#7f8795",
    fontSize: 13,
    fontWeight: 500,
  },
  currencyBadge: {
    minWidth: 34,
    height: 26,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.76)",
    color: "#b43735",
    border: "1px solid rgba(132,42,45,0.16)",
    fontSize: 13,
    fontWeight: 800,
    padding: "0 8px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  currencyMenu: {
    borderRadius: 24,
    border: "1px solid rgba(132,42,45,0.14)",
    background: "rgba(255,253,251,0.94)",
    boxShadow: "0 24px 54px rgba(76,44,51,0.12)",
    backdropFilter: "blur(16px)",
    padding: 12,
    boxSizing: "border-box",
  },
  currencySearch: {
    width: "100%",
    maxWidth: "100%",
    borderRadius: 16,
    border: "1px solid rgba(132,42,45,0.14)",
    padding: "12px 14px",
    fontSize: 14,
    outline: "none",
    marginBottom: 10,
    background: "rgba(255,255,255,0.78)",
    color: "#2f2833",
    boxSizing: "border-box",
    display: "block",
  },
  currencyOptions: {
    maxHeight: 280,
    overflowY: "auto",
    display: "grid",
    gap: 9,
  },
  currencyOption: {
    width: "100%",
    border: "1px solid rgba(132,42,45,0.12)",
    borderRadius: 18,
    background: "rgba(255,255,255,0.82)",
    padding: "12px 13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    textAlign: "left",
    color: "#2f2833",
  },
  currencyOptionActive: {
    borderColor: "rgba(132,42,45,0.28)",
    background: "linear-gradient(180deg, rgba(255,245,241,0.96) 0%, rgba(255,250,248,0.98) 100%)",
    boxShadow: "0 10px 20px rgba(132,42,45,0.08)",
  },
  currencyOptionMain: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  currencyCode: {
    color: "#7e7480",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
  },
  currencyEmpty: {
    padding: "12px 10px",
    color: "#7e7480",
    fontSize: 14,
  },
};
